import {
  PRODUCT_CARDS_META_KEY,
  productCardsSchema,
  type ProductCard,
} from '@inbx/shared/schemas';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  useAssignTicket,
  useAiSuggest,
  useContact,
  useMessages,
  useReplySms,
  useRequestPhone,
  useSendMessage,
  useTemplates,
  useTicketList,
  useUpdateContact,
  useUpdateTicket,
  type ChannelRow,
  type ContactRow,
  type MessageRow,
  type OperatorRow,
  type TicketRow,
} from '../api/queries.js';
import {
  Button,
  ConfirmModal,
  DrawerPanel,
  Field,
  IconButton,
  Input,
  OperatorAvatar,
  PriorityChip,
  SLATimer,
  Select,
  StatusChip,
  Textarea,
  channelLabel,
  formatTime,
  type ChannelType,
  type TicketPriority,
  type TicketStatus,
} from '../components/ui/index.js';

import { onComposerInsert } from './composer-bus.js';

interface Props {
  ticket: TicketRow;
  contact: ContactRow | undefined;
  channel: ChannelRow | undefined;
  assignedOperator: OperatorRow | undefined;
  operators: OperatorRow[];
  workspaceSlug: string | undefined;
  currentUserId: string | undefined;
}

export function ChatPanel(props: Props): JSX.Element {
  const { ticket, workspaceSlug } = props;
  const messages = useMessages(workspaceSlug, ticket.id);
  const sendMessage = useSendMessage(workspaceSlug);
  const replySms = useReplySms(workspaceSlug);
  const requestPhone = useRequestPhone(workspaceSlug);
  const updateTicket = useUpdateTicket(workspaceSlug);
  const updateContact = useUpdateContact(workspaceSlug);
  // Свежий контакт для проверки замены телефона/e-mail — props.contact может быть
  // устаревшим (берётся из списочного источника), а этот запрос инвалидируется
  // при update, поэтому current-значение всегда актуально.
  const liveContactQ = useContact(workspaceSlug, ticket.contactId);
  const liveContact = liveContactQ.data ?? props.contact;
  const aiSuggest = useAiSuggest(workspaceSlug);

  // Привязка распознанных телефона/e-mail к контакту (с предупреждением о замене).
  const [attachReq, setAttachReq] = useState<{
    type: 'phone' | 'email';
    value: string;
    current: string;
  } | null>(null);

  function applyAttach(type: 'phone' | 'email', value: string): void {
    if (!props.contact) return;
    updateContact.mutate({ id: props.contact.id, patch: { [type]: value } });
  }
  function onAttach(type: 'phone' | 'email', value: string): void {
    if (!props.contact) return;
    const current = (type === 'phone' ? liveContact?.phone : liveContact?.email) ?? '';
    const norm = (s: string): string =>
      type === 'phone' ? s.replace(/\D/g, '') : s.trim().toLowerCase();
    // Уже есть другое значение → предупреждаем оператора (заменить? уточни у клиента).
    if (current && norm(current) !== norm(value)) {
      setAttachReq({ type, value, current });
      return;
    }
    applyAttach(type, value);
  }

  const [composeTab, setComposeTab] = useState<'reply' | 'note'>('reply');
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Кнопки в карточке контакта («вставить статус заказа», «карточка товара»)
  // дописывают текст сюда. Именно дописывают: у оператора уже может быть
  // набрана половина ответа, и затирать её нельзя.
  useEffect(
    () =>
      onComposerInsert((insert) => {
        setComposeTab('reply');
        setText((cur) => (cur ? `${cur.replace(/\s+$/, '')} ${insert}` : insert));
        textareaRef.current?.focus();
      }),
    [],
  );

  const [mergeOpen, setMergeOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [resolveConfirm, setResolveConfirm] = useState(false);

  function autoGrow(): void {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(200, el.scrollHeight) + 'px';
  }

  function onSend(): void {
    if (!text.trim()) return;
    sendMessage.mutate({
      ticketId: ticket.id,
      body: text.trim(),
      visibility: composeTab === 'note' ? 'internal_note' : 'public',
    });
    setText('');
    setTimeout(autoGrow, 0);
  }

  function onReplySms(): void {
    if (!text.trim()) return;
    replySms.mutate(
      { ticketId: ticket.id, text: text.trim() },
      { onSuccess: () => setText('') },
    );
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      onSend();
    }
  }

  async function aiSuggestReply(): Promise<void> {
    try {
      const res = await aiSuggest.mutateAsync({ ticketId: ticket.id, tone: 'friendly' });
      setText((cur) => (cur ? cur + '\n' : '') + res.text);
      setTimeout(autoGrow, 0);
    } catch {
      // silent
    }
  }

  const channelType = (props.channel?.type ?? 'custom') as ChannelType;
  const status = ticket.status as TicketStatus;
  const priority = ticket.priority as TicketPriority;

  return (
    <>
      <ChatHeader
        ticket={ticket}
        contact={props.contact}
        channelType={channelType}
        channelName={props.channel?.name}
        onMerge={() => setMergeOpen(true)}
        onAssign={() => setAssignOpen(true)}
        onEscalate={() => setEscalateOpen(true)}
        onResolve={() => setResolveConfirm(true)}
      />

      <StatusBar
        ticket={ticket}
        status={status}
        priority={priority}
        assignedOperator={props.assignedOperator}
        workspaceSlug={workspaceSlug}
      />

      <MessagesArea
        messages={messages.data}
        loading={messages.isLoading}
        contactName={props.contact?.name ?? 'Клиент'}
        operatorsById={new Map(props.operators.map((o) => [o.id, o]))}
        onAttach={props.contact ? onAttach : undefined}
      />

      <QuickReplies
        workspaceSlug={workspaceSlug}
        onPick={(t) => setText((cur) => (cur ? cur + ' ' : '') + t)}
      />

      {channelType === 'partner' && props.channel?.config?.can_reply === false && (
        // Канал магазина принимает платформа; ответить в группу можно только
        // её ключом — что сделать, платформа сказала сама.
        <div
          style={{
            margin: '0 12px 8px',
            padding: '8px 12px',
            borderRadius: 'var(--inbx-radius)',
            background: 'var(--inbx-bg-secondary)',
            border: '1px solid var(--inbx-warning, #d97706)',
            fontSize: 13,
          }}
        >
          <i className="ti ti-alert-triangle" /> Сообщения отсюда видны, но ответить пока нельзя.{' '}
          {String(props.channel.config.reply_hint ?? '')}
        </div>
      )}

      <div className="inbx-compose">
        <div className="inbx-compose__tabs">
          <button
            type="button"
            className={`inbx-compose__tab ${composeTab === 'reply' ? 'is-active' : ''}`}
            onClick={() => setComposeTab('reply')}
          >
            Ответ
          </button>
          <button
            type="button"
            className={`inbx-compose__tab inbx-compose__tab--note ${composeTab === 'note' ? 'is-active' : ''}`}
            onClick={() => setComposeTab('note')}
          >
            <i className="ti-lock" /> Заметка
          </button>
        </div>
        <div className={`inbx-compose__box ${composeTab === 'note' ? 'is-note' : ''}`}>
          <textarea
            ref={textareaRef}
            className="inbx-compose__textarea"
            placeholder={
              composeTab === 'note'
                ? 'Внутренняя заметка — клиент не увидит...'
                : 'Напишите ответ клиенту...'
            }
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              autoGrow();
            }}
            onKeyDown={onKeyDown}
          />
          <div className="inbx-compose__toolbar">
            <IconButton icon="ti-paperclip" tooltip="Вложение" />
            <IconButton icon="ti-mood-smile" tooltip="Эмодзи" />
            <IconButton icon="ti-bolt" tooltip="Шаблон (Ctrl+/)" />
            <IconButton
              icon="ti-sparkles"
              tooltip="ИИ-подсказка"
              onClick={aiSuggestReply}
              disabled={aiSuggest.isPending}
            />
            {props.channel?.type === 'telegram' && composeTab === 'reply' && (
              <IconButton
                icon="ti-phone-plus"
                tooltip="Запросить телефон у клиента"
                onClick={() => requestPhone.mutate(ticket.id)}
                disabled={requestPhone.isPending}
              />
            )}
            {props.contact?.phone && composeTab === 'reply' && (
              <Button
                variant="secondary"
                size="md"
                onClick={onReplySms}
                disabled={!text.trim() || replySms.isPending}
                title={`Отправить SMS на ${props.contact.phone}`}
              >
                {replySms.isPending ? 'SMS…' : 'По SMS'} <i className="ti-message-2" />
              </Button>
            )}
            <Button
              variant="primary"
              size="md"
              className="inbx-compose__send"
              onClick={onSend}
              disabled={!text.trim() || sendMessage.isPending}
            >
              {sendMessage.isPending ? 'Отправка…' : 'Отправить'} <i className="ti-send" />
            </Button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <MergeModal
        open={mergeOpen}
        ticket={ticket}
        workspaceSlug={workspaceSlug}
        onClose={() => setMergeOpen(false)}
      />
      <AssignDrawer
        open={assignOpen}
        ticketId={ticket.id}
        workspaceSlug={workspaceSlug}
        operators={props.operators}
        onClose={() => setAssignOpen(false)}
      />
      <EscalateModal
        open={escalateOpen}
        ticketId={ticket.id}
        workspaceSlug={workspaceSlug}
        operators={props.operators}
        onClose={() => setEscalateOpen(false)}
      />
      <ConfirmModal
        open={resolveConfirm}
        title="Закрыть тикет?"
        description="Тикет будет помечен как решённый. Клиент получит уведомление."
        confirmLabel="Закрыть тикет"
        onConfirm={() => {
          updateTicket.mutate({ id: ticket.id, patch: { status: 'resolved' } });
          setResolveConfirm(false);
        }}
        onCancel={() => setResolveConfirm(false)}
      />

      {/* Предупреждение при замене уже указанного телефона/e-mail */}
      <ConfirmModal
        open={!!attachReq}
        title={attachReq?.type === 'phone' ? 'Заменить телефон?' : 'Заменить e-mail?'}
        description={
          attachReq
            ? `У контакта уже указан ${attachReq.type === 'phone' ? 'телефон' : 'e-mail'}: ${
                attachReq.current
              }. Заменить на ${attachReq.value}? Уточните у клиента, какой рабочий.`
            : ''
        }
        confirmLabel="Заменить"
        variant="danger"
        onConfirm={() => {
          if (attachReq) applyAttach(attachReq.type, attachReq.value);
          setAttachReq(null);
        }}
        onCancel={() => setAttachReq(null)}
      />
    </>
  );
}

// ── B4: Status bar with clickable chips ─────────────────────────────

const STATUS_OPTIONS: TicketStatus[] = ['open', 'pending', 'escalated', 'resolved', 'closed'];
const PRIORITY_OPTIONS: TicketPriority[] = ['low', 'medium', 'high', 'urgent'];

function StatusBar({
  ticket,
  status,
  priority,
  assignedOperator,
  workspaceSlug,
}: {
  ticket: TicketRow;
  status: TicketStatus;
  priority: TicketPriority;
  assignedOperator: OperatorRow | undefined;
  workspaceSlug: string | undefined;
}): JSX.Element {
  const updateTicket = useUpdateTicket(workspaceSlug);
  const [statusOpen, setStatusOpen] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);

  return (
    <div className="inbx-chat__status">
      <span className="inbx-chat__status-label">Статус:</span>
      <div style={{ position: 'relative' }}>
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setStatusOpen((v) => !v);
            setPriorityOpen(false);
          }}
        >
          <StatusChip status={status} />
        </span>
        {statusOpen && (
          <div className="inbx-dropdown" onMouseLeave={() => setStatusOpen(false)}>
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className={`inbx-dropdown__item ${s === status ? 'is-active' : ''}`}
                onClick={() => {
                  updateTicket.mutate({ id: ticket.id, patch: { status: s } });
                  setStatusOpen(false);
                }}
              >
                <StatusChip status={s} />
              </button>
            ))}
          </div>
        )}
      </div>

      <span className="inbx-chat__status-label">Приоритет:</span>
      <div style={{ position: 'relative' }}>
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setPriorityOpen((v) => !v);
            setStatusOpen(false);
          }}
        >
          <PriorityChip priority={priority} />
        </span>
        {priorityOpen && (
          <div className="inbx-dropdown" onMouseLeave={() => setPriorityOpen(false)}>
            {PRIORITY_OPTIONS.map((p) => (
              <button
                key={p}
                type="button"
                className={`inbx-dropdown__item ${p === priority ? 'is-active' : ''}`}
                onClick={() => {
                  updateTicket.mutate({ id: ticket.id, patch: { priority: p } });
                  setPriorityOpen(false);
                }}
              >
                <PriorityChip priority={p} />
              </button>
            ))}
          </div>
        )}
      </div>

      <span className="inbx-chat__status-label">SLA:</span>
      <SLATimer deadline={ticket.slaDeadline} breached={ticket.slaBreached} />
      <span className="inbx-chat__status-right">
        {assignedOperator ? (
          <>
            <span style={{ color: 'var(--inbx-text-tertiary)' }}>Оператор:</span>
            <OperatorAvatar
              user={assignedOperator}
              size="sm"
              status={assignedOperator.status as 'online' | 'busy' | 'away' | 'offline'}
            />
            <span style={{ fontWeight: 500 }}>{assignedOperator.name}</span>
          </>
        ) : (
          <span style={{ color: 'var(--inbx-text-tertiary)' }}>Без оператора</span>
        )}
      </span>
    </div>
  );
}

// ── ChatHeader ───────────────────────────────────────────────────────

function ChatHeader({
  ticket,
  contact,
  channelType,
  channelName,
  onMerge,
  onAssign,
  onEscalate,
  onResolve,
}: {
  ticket: TicketRow;
  contact: ContactRow | undefined;
  channelType: ChannelType;
  /** У канала магазина вид канала один, а сеть и магазин — в названии */
  channelName?: string;
  onMerge: () => void;
  onAssign: () => void;
  onEscalate: () => void;
  onResolve: () => void;
}): JSX.Element {
  const name = contact?.name ?? 'Анонимный клиент';
  return (
    <div className="inbx-chat__header">
      <OperatorAvatar user={{ id: contact?.id ?? ticket.contactId, name }} size="md" />
      <div className="inbx-chat__hinfo">
        <div className="inbx-chat__hname">{name}</div>
        <div className="inbx-chat__hsub">
          {channelType === 'partner' && channelName ? channelName : channelLabel(channelType)} ·
          тикет #{ticket.number}
        </div>
      </div>
      <div className="inbx-chat__hactions">
        <IconButton icon="ti-git-merge" tooltip="Объединить тикеты" onClick={onMerge} />
        <IconButton icon="ti-arrow-forward" tooltip="Перевести оператору" onClick={onAssign} />
        <IconButton icon="ti-urgent" tooltip="Эскалировать" onClick={onEscalate} />
        <IconButton icon="ti-circle-check" tooltip="Закрыть тикет" onClick={onResolve} />
      </div>
    </div>
  );
}

// ── B3 Modals ────────────────────────────────────────────────────────

function MergeModal({
  open,
  ticket,
  workspaceSlug,
  onClose,
}: {
  open: boolean;
  ticket: TicketRow;
  workspaceSlug: string | undefined;
  onClose: () => void;
}): JSX.Element | null {
  const tickets = useTicketList(workspaceSlug, { contact_id: ticket.contactId });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const updateTicket = useUpdateTicket(workspaceSlug);

  if (!open) return null;

  const others = (tickets.data ?? []).filter((t) => t.id !== ticket.id);

  function toggle(id: string): void {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function onMerge(): void {
    updateTicket.mutate({ id: ticket.id, patch: { merge_with: Array.from(selected) } });
    onClose();
  }

  return (
    <div className="inbx-modal-overlay" onClick={onClose}>
      <div className="inbx-modal" onClick={(e) => e.stopPropagation()}>
        <div className="inbx-modal__header">
          <h3>Объединить тикеты</h3>
          <IconButton icon="ti-x" onClick={onClose} />
        </div>
        <div className="inbx-modal__body">
          {others.length === 0 ? (
            <p style={{ color: 'var(--inbx-text-tertiary)' }}>Нет других тикетов этого клиента</p>
          ) : (
            others.map((t) => (
              <label
                key={t.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 0',
                  cursor: 'pointer',
                }}
              >
                <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggle(t.id)} />
                <StatusChip status={t.status as TicketStatus} size="sm" />
                <span>{t.subject ?? `#${t.number}`}</span>
              </label>
            ))
          )}
        </div>
        <div className="inbx-modal__footer">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" onClick={onMerge} disabled={selected.size === 0}>
            Объединить {selected.size > 0 ? `(${selected.size})` : ''}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AssignDrawer({
  open,
  ticketId,
  workspaceSlug,
  operators,
  onClose,
}: {
  open: boolean;
  ticketId: string;
  workspaceSlug: string | undefined;
  operators: OperatorRow[];
  onClose: () => void;
}): JSX.Element {
  const assignTicket = useAssignTicket(workspaceSlug);
  const [search, setSearch] = useState('');

  const filtered = operators.filter(
    (o) =>
      o.name.toLowerCase().includes(search.toLowerCase()) ||
      o.email.toLowerCase().includes(search.toLowerCase()),
  );

  function assign(userId: string): void {
    assignTicket.mutate({ id: ticketId, userId });
    onClose();
  }

  return (
    <DrawerPanel open={open} title="Перевести оператору" onClose={onClose} width={320}>
      <Field label="Поиск оператора">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Имя или email..."
          autoFocus
        />
      </Field>
      <div style={{ marginTop: 12 }}>
        {filtered.map((op) => (
          <button
            key={op.id}
            type="button"
            onClick={() => assign(op.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '8px 12px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderRadius: 6,
              textAlign: 'left',
            }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.background = 'var(--inbx-bg-hover)')
            }
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'none')}
          >
            <OperatorAvatar
              user={op}
              size="sm"
              status={op.status as 'online' | 'busy' | 'away' | 'offline'}
            />
            <div>
              <div style={{ fontWeight: 500 }}>{op.name}</div>
              <div style={{ fontSize: 'var(--inbx-fz-sm)', color: 'var(--inbx-text-tertiary)' }}>
                {op.email}
              </div>
            </div>
          </button>
        ))}
      </div>
    </DrawerPanel>
  );
}

function EscalateModal({
  open,
  ticketId,
  workspaceSlug,
  operators,
  onClose,
}: {
  open: boolean;
  ticketId: string;
  workspaceSlug: string | undefined;
  operators: OperatorRow[];
  onClose: () => void;
}): JSX.Element | null {
  const assignTicket = useAssignTicket(workspaceSlug);
  const updateTicket = useUpdateTicket(workspaceSlug);
  const [supervisorId, setSupervisorId] = useState('');
  const [reason, setReason] = useState('');

  if (!open) return null;

  const supervisors = operators.filter(
    (o) => o.role === 'supervisor' || o.role === 'admin' || o.role === 'owner',
  );

  function onEscalate(): void {
    if (supervisorId) assignTicket.mutate({ id: ticketId, userId: supervisorId });
    updateTicket.mutate({
      id: ticketId,
      patch: { priority: 'urgent', tags: ['escalated'], meta: { escalation_reason: reason } },
    });
    onClose();
  }

  return (
    <div className="inbx-modal-overlay" onClick={onClose}>
      <div className="inbx-modal" onClick={(e) => e.stopPropagation()}>
        <div className="inbx-modal__header">
          <h3>Эскалировать тикет</h3>
          <IconButton icon="ti-x" onClick={onClose} />
        </div>
        <div
          className="inbx-modal__body"
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <Field label="Назначить супервизору">
            <Select value={supervisorId} onChange={(e) => setSupervisorId(e.target.value)}>
              <option value="">— Выберите —</option>
              {supervisors.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Причина эскалации">
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Опишите причину..."
              rows={3}
            />
          </Field>
        </div>
        <div className="inbx-modal__footer">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="danger" onClick={onEscalate} disabled={!reason.trim()}>
            Эскалировать
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── B2: Quick replies — vertical expandable ──────────────────────────

function QuickReplies({
  workspaceSlug,
  onPick,
}: {
  workspaceSlug: string | undefined;
  onPick: (t: string) => void;
}): JSX.Element {
  const templatesQ = useTemplates(workspaceSlug);
  const [expanded, setExpanded] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const chatTemplates = (templatesQ.data ?? []).filter((t) => t.kind === 'chat');

  const staticReplies = [
    'Здравствуйте! Чем могу помочь?',
    'Уточню детали и вернусь',
    'Спасибо за обращение!',
    'Передал в профильный отдел',
    'Проверяю информацию по заказу',
  ];

  const allReplies =
    showTemplates && chatTemplates.length > 0 ? chatTemplates.map((t) => t.name) : staticReplies;

  return (
    <div
      className="inbx-quick-panel"
      style={{
        resize: 'vertical',
        overflow: 'auto',
        minHeight: expanded ? 100 : 36,
        maxHeight: '50%',
        borderTop: '1px solid var(--inbx-border)',
        background: 'var(--inbx-bg-secondary)',
        transition: 'min-height 0.15s',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 12px',
          cursor: 'pointer',
          userSelect: 'none',
          fontSize: 'var(--inbx-fz-sm)',
          color: 'var(--inbx-text-secondary)',
          fontWeight: 500,
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <span>
          <i className="ti-bolt" style={{ marginRight: 4 }} />
          Быстрые ответы
        </span>
        <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={`inbx-quick-panel__tab ${!showTemplates ? 'is-active' : ''}`}
            onClick={() => setShowTemplates(false)}
            style={{
              fontSize: 11,
              padding: '2px 6px',
              borderRadius: 4,
              border: 'none',
              cursor: 'pointer',
              background: !showTemplates ? 'var(--inbx-primary)' : 'transparent',
              color: !showTemplates ? '#fff' : 'inherit',
            }}
          >
            Быстрые
          </button>
          <button
            type="button"
            className={`inbx-quick-panel__tab ${showTemplates ? 'is-active' : ''}`}
            onClick={() => {
              setShowTemplates(true);
              setExpanded(true);
            }}
            style={{
              fontSize: 11,
              padding: '2px 6px',
              borderRadius: 4,
              border: 'none',
              cursor: 'pointer',
              background: showTemplates ? 'var(--inbx-primary)' : 'transparent',
              color: showTemplates ? '#fff' : 'inherit',
            }}
          >
            + Шаблоны
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--inbx-text-tertiary)',
            }}
          >
            <i className={expanded ? 'ti-chevron-down' : 'ti-chevron-up'} />
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0 12px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {allReplies.map((r) => (
            <button
              key={r}
              type="button"
              className="inbx-quick__chip"
              style={{ textAlign: 'left', whiteSpace: 'normal' }}
              onClick={() => onPick(r)}
            >
              {r}
            </button>
          ))}
          {showTemplates && chatTemplates.length === 0 && (
            <span style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
              Нет шаблонов типа «чат»
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Распознавание контактных данных в тексте сообщений ──────────────
// Регулярки ищут совпадения ВНУТРИ текста (matchAll), а не сравнивают строку целиком —
// телефон/e-mail могут быть окружены любым текстом. E-mail без захвата окружающей
// пунктуации; телефон — последовательность цифр с +, пробелами, скобками, дефисами.
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const PHONE_RE = /[+]?\d[\d\s()-]{8,16}\d/g;

export interface DetectedEntity {
  type: 'phone' | 'email';
  value: string;
}

/** Находит телефоны/e-mail в тексте — для кнопки «привязать к контакту». */
export function detectEntities(text: string | null | undefined): DetectedEntity[] {
  if (!text) return [];
  const out: DetectedEntity[] = [];
  for (const m of text.matchAll(EMAIL_RE)) out.push({ type: 'email', value: m[0].toLowerCase() });
  for (const m of text.matchAll(PHONE_RE)) {
    const digits = m[0].replace(/\D/g, '');
    if (digits.length >= 10 && digits.length <= 15) out.push({ type: 'phone', value: m[0].trim() });
  }
  return out.filter(
    (e, i, a) => a.findIndex((x) => x.type === e.type && x.value === e.value) === i,
  );
}

// ── MessagesArea ─────────────────────────────────────────────────────

function MessagesArea({
  messages,
  loading,
  contactName,
  operatorsById,
  onAttach,
}: {
  messages: MessageRow[] | undefined;
  loading: boolean;
  contactName: string;
  operatorsById: Map<string, OperatorRow>;
  onAttach?: (type: 'phone' | 'email', value: string) => void;
}): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages?.length]);

  const grouped = useMemo(() => {
    if (!messages) return [];
    const byDate: Array<{ date: string; items: MessageRow[] }> = [];
    for (const m of messages) {
      const d = new Date(m.sentAt).toLocaleDateString('ru-RU');
      const last = byDate[byDate.length - 1];
      if (!last || last.date !== d) byDate.push({ date: d, items: [m] });
      else last.items.push(m);
    }
    return byDate;
  }, [messages]);

  if (loading) {
    return <div className="inbx-chat__messages">Загрузка…</div>;
  }
  if (!messages || messages.length === 0) {
    return (
      <div className="inbx-chat__messages">
        <div className="inbx-empty" style={{ margin: 'auto' }}>
          <i className="ti-message-off inbx-empty__icon" />
          <div className="inbx-empty__title">Сообщений пока нет</div>
          <div className="inbx-empty__desc">Это будет первое сообщение по тикету</div>
        </div>
      </div>
    );
  }

  return (
    <div className="inbx-chat__messages" ref={scrollRef}>
      {grouped.map((g) => (
        <div key={g.date}>
          <div className="inbx-chat__date">
            {g.date === new Date().toLocaleDateString('ru-RU') ? 'Сегодня' : g.date}
          </div>
          {g.items.map((m) => (
            <Message
              key={m.id}
              m={m}
              contactName={contactName}
              operatorsById={operatorsById}
              onAttach={onAttach}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function Message({
  m,
  contactName,
  operatorsById,
  onAttach,
}: {
  m: MessageRow;
  contactName: string;
  operatorsById: Map<string, OperatorRow>;
  onAttach?: (type: 'phone' | 'email', value: string) => void;
}): JSX.Element {
  const isNote = m.visibility === 'internal_note';
  const isIn = m.direction === 'in';
  const cls = isNote ? 'inbx-msg--note' : isIn ? 'inbx-msg--in' : 'inbx-msg--out';
  const operator = m.senderUserId ? operatorsById.get(m.senderUserId) : undefined;
  // Распознанные телефон/e-mail — предлагаем привязать к контакту (только во входящих).
  const detected = isIn && onAttach ? detectEntities(m.body) : [];
  const products = productsOf(m);
  return (
    <div className={`inbx-msg ${cls}`}>
      {isIn && (
        <OperatorAvatar user={{ id: m.senderContactId ?? 'c', name: contactName }} size="xs" />
      )}
      <div>
        {products ? (
          <MessageProducts products={products} />
        ) : (
          <div className="inbx-msg__bubble">{renderBoldMarkdown(m.body)}</div>
        )}
        {detected.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {detected.map((e) => (
              <button
                key={e.type + e.value}
                type="button"
                onClick={() => onAttach?.(e.type, e.value)}
                title="Привязать к контакту"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 8px',
                  fontSize: 12,
                  borderRadius: 999,
                  border: '1px solid var(--inbx-border)',
                  background: 'var(--inbx-bg-secondary)',
                  cursor: 'pointer',
                }}
              >
                <i className={e.type === 'phone' ? 'ti-phone' : 'ti-mail'} />
                {e.value}
                <span style={{ color: 'var(--inbx-accent)' }}>· привязать</span>
              </button>
            ))}
          </div>
        )}
        <div className="inbx-msg__meta">
          {isNote && (
            <>
              <i className="ti-lock" /> только для операторов
            </>
          )}
          {!isNote && !isIn && (
            <>
              <i
                className="ti-check"
                style={{ color: m.readAt ? 'var(--inbx-success)' : 'var(--inbx-text-tertiary)' }}
              />
              {operator ? `${operator.name} · ` : ''}
            </>
          )}
          {formatTime(m.sentAt)}
        </div>
        {!isNote && !isIn && deliveryOf(m)?.status === 'failed' && (
          <div
            className="inbx-msg__meta"
            style={{ color: 'var(--inbx-danger, #dc2626)' }}
            title={deliveryOf(m)?.error}
          >
            <i className="ti ti-alert-circle" /> не доставлено: {deliveryOf(m)?.error ?? 'без пояснения'}
          </div>
        )}
      </div>
    </div>
  );
}

/** Итог доставки ответа (пишет отправка в канал): failed — оператор видит причину. */
function deliveryOf(m: MessageRow): { status: 'sent' | 'failed'; error?: string } | null {
  const d = m.meta?.delivery as { status?: string; error?: string } | undefined;
  if (d?.status !== 'sent' && d?.status !== 'failed') return null;
  return { status: d.status, ...(d.error ? { error: d.error } : {}) };
}

/**
 * Безопасный рендер minimal-markdown: только **жирный**. НИКАКОГО innerHTML —
 * только React text-nodes + <strong>, поэтому XSS невозможен (любые `<script>`
 * в сообщении пользователя экранируются React-ом автоматически).
 */
/** Карточки товаров, отправленные покупателю (meta.products). */
function productsOf(m: MessageRow): ProductCard[] | null {
  const parsed = productCardsSchema.safeParse(m.meta?.[PRODUCT_CARDS_META_KEY]);
  return parsed.success ? parsed.data : null;
}

/**
 * Оператор видит то же, что покупатель в виджете, — карточки, а не текстовую
 * замену: иначе непонятно, что именно ушло.
 */
function MessageProducts({ products }: { products: ProductCard[] }): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxWidth: 420 }}>
      {products.map((p) => (
        <div
          key={p.external_id}
          className="inbx-card"
          style={{ width: 132, padding: 6, display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          {p.image_url && (
            <img
              src={p.image_url}
              alt=""
              style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 4 }}
            />
          )}
          <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>{p.title}</div>
          {p.price && (
            <div style={{ fontSize: 12 }}>
              {Number(p.price.amount).toLocaleString('ru-RU')} {p.price.currency === 'RUB' ? '₽' : p.price.currency}
            </div>
          )}
          {p.url && (
            <a href={p.url} target="_blank" rel="noreferrer noopener" style={{ fontSize: 12 }}>
              открыть
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

function renderBoldMarkdown(text: string | null | undefined): React.ReactNode {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
}
